import { Timer } from 'lucide-react'
import { type ReactElement, useEffect, useState } from 'react'

import { cn } from '@/lib/utils'

/**
 * Contagem regressiva até um instante (epoch ms).
 *
 * O desafio de 2FA vale 5 minutos. Sem contador, o usuário que foi buscar o
 * celular volta, digita o código certo e leva "desafio inválido" — parece bug
 * do sistema, e é só o relógio. Mostrar o tempo restante transforma o erro em
 * informação.
 */

/** Segundos restantes até `expiraEm`. Atualiza a cada segundo, nunca negativo. */
export function useSegundosRestantes(expiraEm: number | null): number {
  const calcular = (): number =>
    expiraEm === null ? 0 : Math.max(0, Math.ceil((expiraEm - Date.now()) / 1000))

  const [segundos, setSegundos] = useState(calcular)

  useEffect(() => {
    if (expiraEm === null) {
      setSegundos(0)
      return
    }

    // Recalcula na hora para não exibir o valor do desafio anterior por 1s.
    setSegundos(Math.max(0, Math.ceil((expiraEm - Date.now()) / 1000)))

    const intervalo = window.setInterval(() => {
      const restante = Math.max(0, Math.ceil((expiraEm - Date.now()) / 1000))
      setSegundos(restante)

      if (restante === 0) {
        window.clearInterval(intervalo)
      }
    }, 1000)

    return () => {
      window.clearInterval(intervalo)
    }
  }, [expiraEm])

  return segundos
}

/** `330` → `5:30`. */
export function formatarDuracao(segundos: number): string {
  const minutos = Math.floor(segundos / 60)
  const resto = segundos % 60

  return `${String(minutos)}:${String(resto).padStart(2, '0')}`
}

export interface ContagemRegressivaProps {
  readonly segundos: number
  readonly className?: string
}

export function ContagemRegressiva({ segundos, className }: ContagemRegressivaProps): ReactElement {
  const expirado = segundos === 0

  return (
    <p
      className={cn(
        'flex items-center gap-1.5 text-xs',
        expirado ? 'text-danger-fg' : 'text-fg-muted',
        className,
      )}
      // `polite` e não `assertive`: o contador não pode interromper o leitor de
      // tela a cada segundo.
      aria-live="polite"
    >
      <Timer className="size-3.5" aria-hidden="true" />
      {expirado ? (
        'O código expirou. Entre novamente para receber um novo desafio.'
      ) : (
        <>
          Este desafio expira em <span className="tabular-money">{formatarDuracao(segundos)}</span>
        </>
      )}
    </p>
  )
}
